import type {
  PostSessionAiBoothSettings,
  PostSessionAiTarget,
  PostSessionAiTier,
} from '@renderer/types/configState'

const TARGETS: PostSessionAiTarget[] = ['strip', 'raw']
const TIERS: PostSessionAiTier[] = ['fast', 'quality', 'studio']

function httpsPreviewUrl(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined
  const t = raw.trim()
  if (!t || t.length > 2048) return undefined
  try {
    const u = new URL(t)
    if (u.protocol !== 'https:') return undefined
    return t
  } catch {
    return undefined
  }
}

/**
 * Normalize dashboard postSessionAi for booth reads. Coerces legacy `target: "both"` to
 * `"strip"` and mirrors the active target's preview pair onto the legacy single keys.
 */
export function resolvePostSessionAiBooth(raw: unknown): PostSessionAiBoothSettings | null {
  if (raw === undefined || raw === null) return null
  if (typeof raw !== 'object' || Array.isArray(raw)) return null

  const o = raw as Record<string, unknown>

  let target: PostSessionAiTarget = 'strip'
  if (typeof o.target === 'string') {
    if (TARGETS.includes(o.target as PostSessionAiTarget)) {
      target = o.target as PostSessionAiTarget
    } else if (o.target === 'both') {
      target = 'strip'
    }
  }

  let tier: PostSessionAiTier = 'fast'
  if (typeof o.tier === 'string' && TIERS.includes(o.tier as PostSessionAiTier)) {
    tier = o.tier as PostSessionAiTier
  }

  const legacyBefore = httpsPreviewUrl(o.previewBeforeUrl)
  const legacyAfter = httpsPreviewUrl(o.previewAfterUrl)
  const stripBefore = httpsPreviewUrl(o.previewBeforeUrlStrip)
  const stripAfter = httpsPreviewUrl(o.previewAfterUrlStrip)
  const rawBefore = httpsPreviewUrl(o.previewBeforeUrlRaw)
  const rawAfter = httpsPreviewUrl(o.previewAfterUrlRaw)

  const previewBeforeUrlStrip = stripBefore ?? (target === 'strip' ? legacyBefore : undefined)
  const previewAfterUrlStrip = stripAfter ?? (target === 'strip' ? legacyAfter : undefined)
  const previewBeforeUrlRaw = rawBefore ?? (target === 'raw' ? legacyBefore : undefined)
  const previewAfterUrlRaw = rawAfter ?? (target === 'raw' ? legacyAfter : undefined)

  const previewBeforeUrl = target === 'strip' ? previewBeforeUrlStrip : previewBeforeUrlRaw
  const previewAfterUrl = target === 'strip' ? previewAfterUrlStrip : previewAfterUrlRaw

  const effectPrompt = typeof o.effectPrompt === 'string' ? o.effectPrompt : ''
  const ownerPrompt =
    typeof o.ownerPrompt === 'string' && o.ownerPrompt.trim()
      ? o.ownerPrompt
      : effectPrompt

  const out: PostSessionAiBoothSettings = {
    enabled: Boolean(o.enabled),
    target,
    tier,
    ownerPrompt,
    allowGuestPrompt: Boolean(o.allowGuestPrompt),
    guestPromptMaxLength:
      typeof o.guestPromptMaxLength === 'number' && o.guestPromptMaxLength >= 0
        ? o.guestPromptMaxLength
        : 0,
  }

  if (previewBeforeUrl !== undefined) out.previewBeforeUrl = previewBeforeUrl
  if (previewAfterUrl !== undefined) out.previewAfterUrl = previewAfterUrl
  if (previewBeforeUrlStrip !== undefined) out.previewBeforeUrlStrip = previewBeforeUrlStrip
  if (previewAfterUrlStrip !== undefined) out.previewAfterUrlStrip = previewAfterUrlStrip
  if (previewBeforeUrlRaw !== undefined) out.previewBeforeUrlRaw = previewBeforeUrlRaw
  if (previewAfterUrlRaw !== undefined) out.previewAfterUrlRaw = previewAfterUrlRaw

  return out
}
